import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { agentListPath, useApi } from "../api/client";
import type { AgentCard, AgentListResponse } from "../api/types";
import { IconTile } from "../components/AgentBits";
import { BarChart, Meter } from "../components/Charts";
import { ConfidenceBadge } from "../components/ConfidenceBadge";
import { Empty, ErrorState, Loading } from "../components/States";
import {
  SubmitterFilter,
  useSubmittedBy,
  withSubmitter,
} from "../components/SubmitterFilter";
import { Breadcrumb, SegmentedControl, Toolbar, type Segment } from "../components/Toolbar";
import { downloadCsv, stampedName, toCsv } from "../lib/csv";
import { EM_DASH, fmtCost, fmtCostCompact, fmtDateTime, fmtMs, fmtTokens } from "../lib/format";

type StatusKey = "all" | "active" | "idle";

const STATUS_SEGMENTS: Segment<StatusKey>[] = [
  { key: "all", label: "All" },
  { key: "active", label: "Active" },
  { key: "idle", label: "Idle" },
];

export function AgentPanel() {
  const [q, setQ] = useState("");
  const [status, setStatus] = useState<StatusKey>("all");
  const navigate = useNavigate();
  const [submittedBy, setSubmittedBy] = useSubmittedBy();
  const { data, error, loading, reload } = useApi<AgentListResponse>(
    agentListPath(submittedBy || undefined),
  );

  const match = (a: AgentCard) => {
    if (status === "active" && a.runs === 0) return false;
    if (status === "idle" && a.runs > 0) return false;
    if (!q) return true;
    const needle = q.toLowerCase();
    return (
      a.label.toLowerCase().includes(needle) ||
      a.description.toLowerCase().includes(needle) ||
      a.tags.some((t) => t.toLowerCase().includes(needle))
    );
  };

  // Agents that have run come first, busiest first; the idle ones keep their
  // registry order behind them. Alphabetical put Address Proof — which has
  // never run — in the first slot and Trade Licence, which carries most of the
  // traffic, in the tenth. The "Active" and "Idle" segments above still narrow
  // the list; this only decides what an operator sees before they filter.
  const live = useMemo(
    () =>
      (data?.items ?? [])
        .filter(match)
        .slice()
        .sort((x, y) => (y.runs > 0 ? 1 : 0) - (x.runs > 0 ? 1 : 0) || y.runs - x.runs),
    [data, q, status]
  );

  // Charts cover only agents that have actually run: a bar of height zero for
  // ten idle agents crowds out the four that carry the traffic, and says
  // nothing the "Idle" status on the card doesn't already say.
  const withRuns = useMemo(
    () => (data?.items ?? []).filter((a) => a.runs > 0).sort((x, y) => y.runs - x.runs),
    [data]
  );

  const totals = useMemo(() => {
    const items = data?.items ?? [];
    const priced = items.filter((a) => a.cost_usd !== null);
    return {
      agents: items.length,
      active: items.filter((a) => a.runs > 0).length,
      runs: items.reduce((s, a) => s + a.runs, 0),
      tokens: items.reduce((s, a) => s + a.total_tokens, 0),
      cost: priced.length ? priced.reduce((s, a) => s + (a.cost_usd ?? 0), 0) : null,
      unpriced: items.filter((a) => a.runs > 0 && a.cost_usd === null).length,
    };
  }, [data]);

  const exportCsv = () => {
    if (!live.length) return;
    downloadCsv(
      stampedName("agents"),
      toCsv<AgentCard>(live, [
        { header: "Agent", value: (a) => a.label },
        { header: "Document type", value: (a) => a.document_type },
        { header: "Class", value: (a) => a.agent_name },
        { header: "Status", value: (a) => a.status },
        { header: "Extractions", value: (a) => a.runs },
        { header: "Failures", value: (a) => a.failures },
        { header: "Avg confidence %", value: (a) => a.avg_confidence },
        // Separate columns, never one "score". The two measure different
        // things, and a spreadsheet given one column would happily average
        // them together.
        { header: "Required fields score %", value: (a) => a.requirements.score },
        { header: "Runs with all fields", value: (a) => a.requirements.complete },
        { header: "Scored fields", value: (a) => a.requirements.field_count },
        { header: "p50 latency ms", value: (a) => a.p50_latency_ms },
        { header: "p95 latency ms", value: (a) => a.p95_latency_ms },
        { header: "Model", value: (a) => a.model },
        { header: "Tokens", value: (a) => a.total_tokens },
        { header: "Cost USD", value: (a) => a.cost_usd },
        { header: "Last run", value: (a) => a.last_run_at },
      ])
    );
  };

  return (
    <>
      <Breadcrumb trail={[{ label: "Agents" }]} />

      <div className="page-head">
        <div>
          <h1 className="page-title">AI Agents control panel</h1>
          <p className="page-sub">Manage, configure and monitor all document AI agents</p>
        </div>
        <div className="range-group">
          <SubmitterFilter value={submittedBy} onChange={setSubmittedBy} idPrefix="agents" />
          <SegmentedControl
            segments={STATUS_SEGMENTS}
            value={status}
            onChange={setStatus}
            label="Agent status"
          />
        </div>
      </div>

      <Toolbar
        title="Registered Agents"
        subtitle="Extraction volume, confidence and cost per agent"
        onExport={exportCsv}
        exportDisabled={!live.length}
        search={q}
        onSearch={setQ}
        searchPlaceholder="Search agents"
        right={
          <>
            <button onClick={() => navigate(withSubmitter("/documents", submittedBy))}>
              Activity log
            </button>
            <button
              title="Agents are registered in code, under ppd_agent_service/src/agents/. There is no runtime creation path."
              disabled
            >
              + New agent
            </button>
          </>
        }
      />

      {loading ? (
        <div className="card"><Loading what="agents" /></div>
      ) : error ? (
        <div className="card"><ErrorState message={error} onRetry={reload} /></div>
      ) : !data ? null : (
        <>
          <div className="tiles">
            <div className="tile">
              <div className="tile-label">Registered agents</div>
              <div className="tile-value">{totals.agents}</div>
              <div className="tile-note">{totals.active} have run</div>
            </div>
            <div className="tile">
              <div className="tile-label">Extractions</div>
              <div className="tile-value">{fmtTokens(totals.runs)}</div>
              <div className="tile-note">across all agents</div>
            </div>
            <div className="tile">
              <div className="tile-label">Tokens</div>
              <div className="tile-value">{fmtTokens(totals.tokens)}</div>
              <div className="tile-note">recorded in the ledger</div>
            </div>
            <div className="tile">
              <div className="tile-label">Cost</div>
              <div className="tile-value">{fmtCost(totals.cost)}</div>
              <div className={`tile-note${totals.unpriced ? " warn" : ""}`}>
                {totals.unpriced
                  ? `lower bound — ${totals.unpriced} agent(s) unrecorded`
                  : "all runs priced"}
              </div>
            </div>
          </div>

          <div className="notice">
            <strong>Confidence, not accuracy.</strong> The figure on each card is the model's
            self-reported confidence. Accuracy would need labelled ground truth, which this
            platform does not collect — so no accuracy number is shown rather than an invented one.
          </div>

          {withRuns.length ? (
            <div className="grid-2">
              <div className="card">
                <div className="card-head bordered">
                  <span className="card-title">Extractions by agent</span>
                  <span className="muted">agents that have run</span>
                </div>
                <div className="card-body">
                  <BarChart
                    data={withRuns.map((a) => ({ label: a.initials, sub: a.label, value: a.runs }))}
                    valueLabel="Extractions"
                    height={200}
                  />
                </div>
              </div>

              <div className="card">
                <div className="card-head bordered">
                  <span className="card-title">Average confidence by agent</span>
                  <span className="muted">self-reported</span>
                </div>
                <div className="card-body">
                  {withRuns.some((a) => a.avg_confidence !== null) ? (
                    withRuns.map((a) =>
                      a.avg_confidence === null ? (
                        <Meter
                          key={a.key}
                          label={a.label}
                          value={0}
                          display="— none reported"
                          color="var(--border-strong)"
                        />
                      ) : (
                        <Meter
                          key={a.key}
                          label={a.label}
                          value={a.avg_confidence}
                          display={`${a.avg_confidence.toFixed(1)}%`}
                          color={
                            a.avg_confidence >= 90 ? "var(--good)"
                            : a.avg_confidence >= 75 ? "var(--warning)"
                            : a.avg_confidence >= 50 ? "var(--serious)"
                            : "var(--critical)"
                          }
                        />
                      )
                    )
                  ) : (
                    <Empty title="No confidence scores recorded" />
                  )}
                </div>
              </div>
            </div>
          ) : null}

          <div className="section-title">
            Agents <span className="pill">{data.live_count}</span>
            {!data.registry_reachable ? (
              <span className="pill pill-warning" title="ppd_agent_service was unreachable, so this list is built from agents that have run rather than from the registry. Some registered agents may be missing.">
                registry unreachable
              </span>
            ) : null}
          </div>

          {live.length === 0 ? (
            <div className="card"><Empty title="No agents match that search" /></div>
          ) : (
            <div className="agent-grid">
              {live.map((a) => (
                <AgentGridCard
                  key={a.key}
                  agent={a}
                  onOpen={() =>
                    navigate(withSubmitter(`/agents/${a.document_type ?? a.key}`, submittedBy))
                  }
                />
              ))}
            </div>
          )}

          {data.retired_types.length > 0 ? (
            <div className="notice" style={{ marginTop: 16, marginBottom: 0 }}>
              {data.retired_types.length} historical document type
              {data.retired_types.length === 1 ? "" : "s"} (
              {data.retired_types.map((r) => `${r.document_type}: ${r.runs} run(s)`).join(", ")})
              {data.retired_types.length === 1 ? " has" : " have"} extractions on record but no
              registered agent, so {data.retired_types.length === 1 ? "it is" : "they are"} not
              listed above. Those extractions are still visible under Documents.
            </div>
          ) : null}
        </>
      )}
    </>
  );
}

/**
 * One agent in the grid.
 *
 * The card shows only what it can actually say. An agent that has never run
 * has no model, no latency and no cost, and the previous version reported that
 * one fact three times over — "no model yet", "no runs", and "0 runs — p50
 * cost —" — which filled three quarters of the grid with em dashes and pushed
 * the agents doing the work into the visual background. Idle now gets a single
 * line, and the metrics appear only where there are metrics.
 */
function AgentGridCard({ agent: a, onOpen }: { agent: AgentCard; onOpen: () => void }) {
  const idle = a.runs === 0;

  return (
    <button
      className="agent-card"
      data-idle={idle}
      style={{ ["--card-accent" as string]: `var(--a-${a.accent})` }}
      onClick={onOpen}
      aria-label={`Open ${a.label}`}
    >
      <span className="agent-card-top">
        <IconTile initials={a.initials} accent={a.accent} />

        {/* Failures take the slot when there are any. An agent at 68%
            confidence with two failed extractions is the first thing on this
            card an operator needs to see, and until now it was not on the card
            at all.

            Except on the triage agent, where a non-completed run IS the
            intended outcome. It sits at 16 of 16, and a red "16 failed" there
            would be a false alarm about an agent doing its job — the same
            mistake as painting eight idle cards amber. */}
        {a.failures > 0 && !a.fallback ? (
          <span
            className="pill pill-critical"
            title={`${a.failures} of ${a.runs} extractions did not complete`}
          >
            {a.failures} failed
          </span>
        ) : a.fallback && a.runs > 0 ? (
          <span
            className="pill"
            title="This agent triages documents the classifier could not place and routes them to manual review. It does not extract, so none of its runs completes an extraction."
          >
            triage
          </span>
        ) : (
          <span
            className="agent-card-status"
            data-state={idle ? "idle" : "active"}
            title={
              idle
                ? "Registered and dispatchable, but no document of this type has arrived yet."
                : `Last run ${fmtDateTime(a.last_run_at)}`
            }
          >
            <span className={`dot dot-${idle ? "idle" : "active"}`} aria-hidden="true" />
            {idle ? "Idle" : "Active"}
          </span>
        )}
      </span>

      <h3>{a.label}</h3>
      <p title={a.description}>{a.description}</p>

      {idle ? (
        <span className="agent-card-idle">
          Not yet run — no model, latency or cost to report
        </span>
      ) : (
        <>
          <span className="agent-card-foot">
            <span
              className="pill pill-mono"
              title={
                a.model
                  ? `Model on the most recent extraction: ${a.model}`
                  : "No model is recorded against these runs. This agent reached a verdict without calling one."
              }
            >
              {a.model ?? "no model recorded"}
            </span>
            <ConfidenceBadge
              value={a.avg_confidence}
              absentReason="No confidence score was reported on any of this agent's runs"
            />
          </span>

          <span className="agent-stats">
            {/* Both scores, side by side and never merged. Confidence is the
                model's self-report; this is the share of runs that produced
                every field the agent is scored on, which is checked rather
                than reported. A card showing only the first invites a
                self-report to be read as a result. */}
            <span className="agent-stat">
              <span
                className={`agent-stat-value${
                  a.requirements.score === null ? " absent" : ""
                }`}
              >
                {a.requirements.score === null
                  ? EM_DASH
                  : `${a.requirements.score}%`}
              </span>
              <span
                className="agent-stat-label"
                title={
                  !a.requirements.defined
                    ? "No fields are scored for this agent yet, so it has no score."
                    : a.requirements.score === null
                      ? "This agent has no extractions to score against yet."
                      : `${a.requirements.complete} of ${a.requirements.scored_runs} runs produced all ${a.requirements.field_count} scored field(s)`
                }
              >
                {a.requirements.defined ? "fields" : "unscored"}
              </span>
            </span>
            <span className="agent-stat">
              <span className="agent-stat-value">{fmtTokens(a.runs)}</span>
              <span className="agent-stat-label">runs</span>
            </span>
            <span className="agent-stat">
              <span
                className={`agent-stat-value${a.p50_latency_ms === null ? " absent" : ""}`}
              >
                {fmtMs(a.p50_latency_ms)}
              </span>
              <span className="agent-stat-label">p50</span>
            </span>
            <span className="agent-stat">
              <span className={`agent-stat-value${a.cost_usd === null ? " absent" : ""}`}>
                {fmtCostCompact(a.cost_usd)}
              </span>
              <span
                className="agent-stat-label"
                title={
                  a.cost_usd === null
                    ? "Tokens were spent but no rate is on file for this model, so no cost can be stated. It is not zero."
                    : a.unpriced_calls
                      ? `Lower bound — ${a.unpriced_calls} call(s) have no rate on file`
                      : "Total across every run"
                }
              >
                {a.cost_usd !== null && a.unpriced_calls ? "cost ≥" : "cost"}
              </span>
            </span>
          </span>
        </>
      )}
    </button>
  );
}
